/**
 * IAR + Keanggotaan + Downloads — single card with bordered grids.
 */

import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileText, Calendar, Globe, Shield, UserCircle, Hash, Fingerprint, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import type { User } from "@/services/authService";
import { userService } from "@/services/userService";
import { paymentService } from "@/services/paymentService";
import { toast } from "sonner";
import {
  formatDate,
  formatMasaLakuLikeKtaPdf,
  shouldShowMasaLakuKtaInIarSection,
} from "../utils/userDetailUtils";
import { formatIarType, formatStatusKeanggotaan } from "../utils/statusUtils";
import { NriClaimDialog } from "./NriClaimDialog";
import { apiFetch } from "@/lib/apiFetch";
import { apiConfig } from "@/config/api";

interface IarKeanggotaanTabProps {
  user: User;
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={cn("p-4", className)}>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm font-medium text-foreground">{value || "-"}</div>
    </div>
  );
}

import { cn } from "@/lib/utils";

export function IarKeanggotaanTab({ user }: IarKeanggotaanTabProps) {
  const [downloadingKTA, setDownloadingKTA] = React.useState(false);
  const [downloadingTag, setDownloadingTag] = React.useState(false);
  const [downloadingCallsign, setDownloadingCallsign] = React.useState(false);
  const [hasSettlement, setHasSettlement] = React.useState(false);
  const [checkingSettlement, setCheckingSettlement] = React.useState(true);
  const [settlementMasaLakuKta, setSettlementMasaLakuKta] = React.useState<string | null>(null);

  const [claimDialogOpen, setClaimDialogOpen] = React.useState(false);
  const [pendingRequest, setPendingRequest] = React.useState<any>(null);
  const [checkingPending, setCheckingPending] = React.useState(true);

  const showMasaLakuKta = shouldShowMasaLakuKtaInIarSection(user.tipe_iar, user.masa_laku);
  const isIarKehormatan = !user.tipe_iar || user.tipe_iar === "iar_kehormatan";
  const today = Date.now();
  const userMasaLakuKta = user.masa_laku_kta ? new Date(user.masa_laku_kta).getTime() : 0;
  const settlementMasaLakuKtaMs = settlementMasaLakuKta ? new Date(settlementMasaLakuKta).getTime() : 0;
  const effectiveMasaLakuKta = userMasaLakuKta || settlementMasaLakuKtaMs;
  const hasValidSdppi = String(user.status_keanggotaan ?? "") === "1" && effectiveMasaLakuKta > today;
  const canDownloadDocs = hasValidSdppi || isIarKehormatan
    || (String(user.status_keanggotaan ?? "") !== "0" && hasSettlement);

  React.useEffect(() => {
    if (hasValidSdppi || isIarKehormatan) {
      setCheckingSettlement(false);
      return;
    }
    if (user.id_client && user.callsign) {
      setCheckingSettlement(true);
      paymentService.checkSettlement(user.id_client, user.callsign)
        .then((result) => {
          setHasSettlement(result.hasSettlement);
          setSettlementMasaLakuKta(result.masa_laku_kta);
        })
        .finally(() => setCheckingSettlement(false));
    }
  }, [user.id_client, user.callsign, hasValidSdppi, isIarKehormatan]);

  const checkPendingRequest = React.useCallback(async () => {
    try {
      const res = await apiFetch(`${apiConfig.baseURL}/api/nri-requests/me/pending`);
      const data = await res.json();
      setPendingRequest(data.data || null);
    } catch {
      setPendingRequest(null);
    } finally {
      setCheckingPending(false);
    }
  }, []);

  React.useEffect(() => {
    checkPendingRequest();
  }, [checkPendingRequest]);

  const handleDownload = async (type: "kta" | "tag" | "callsign") => {
    if (!user.id) { toast.error("User ID tidak d found"); return; }
    const setters = { kta: setDownloadingKTA, tag: setDownloadingTag, callsign: setDownloadingCallsign };
    const labels = { kta: "KTA", tag: "Tag", callsign: "Papan Callsign" };
    setters[type](true);
    try {
      const fns = { kta: userService.downloadKTA, tag: userService.downloadTag, callsign: userService.downloadCallsign };
      await fns[type](user.id, user.callsign);
      toast.success(`${labels[type]} berhasil diunduh`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Gagal mengunduh ${labels[type]}`);
    } finally {
      setters[type](false);
    }
  };

  const hasForeignIar = user.callsign_negara_asal || user.iar_negara_asal || user.masa_laku_negara_asal;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <CardTitle>IAR &amp; Keanggotaan</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          {user.status_callsign && (
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" />
              {user.status_callsign}
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <UserCircle className="h-3 w-3" />
            {formatStatusKeanggotaan(user.status_keanggotaan)}
          </Badge>
          {user.tipe_iar && (
            <Badge variant="outline" className="gap-1">
              <FileText className="h-3 w-3" />
              {formatIarType(user.tipe_iar)}
            </Badge>
          )}
        </div>

        {/* IAR Info Grid */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Informasi IAR
          </h3>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3">
              <Field label="IAR" value={user.iar || "-"} className="border-b sm:border-r border-border" />
              <Field label="Tanggal Terbit" value={formatDate(user.tanggal_terbit)} className="border-b sm:border-r border-border" />
              <Field label="Masa Laku IAR" value={formatDate(user.masa_laku)} className="border-b border-border" />
              <Field label="Masa berlaku (cetakan KTA)" value={formatMasaLakuLikeKtaPdf(user.masa_laku, user.masa_laku_kta)} className={cn("border-b border-border", showMasaLakuKta ? "sm:border-r" : "")} />
              {showMasaLakuKta && (
                <Field label="Masa Laku KTA (SDPPI)" value={formatDate(user.masa_laku_kta)} className="border-b border-border" />
              )}
              <Field label="Jenis Permohonan" value={user.jenis_permohonan || "-"} className="" />
            </div>
          </div>
          {showMasaLakuKta && (
            <p className="text-xs text-muted-foreground mt-2 leading-snug">
              Jika Masa Laku IAR berakhir 2100 / seumur hidup, PDF KTA memakai tanggal dari Masa Laku KTA.
            </p>
          )}
        </div>

        {/* Foreign IAR */}
        {hasForeignIar && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              IAR Negara Asal
            </h3>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid sm:grid-cols-3">
                <Field label="Callsign Negara Asal" value={user.callsign_negara_asal || "-"} className="border-b sm:border-r border-border" />
                <Field label="IAR Negara Asal" value={user.iar_negara_asal || "-"} className="border-b sm:border-r border-border" />
                <Field label="Masa Laku" value={formatDate(user.masa_laku_negara_asal)} className="border-b border-border" />
              </div>
            </div>
          </div>
        )}

        {/* Membership Info Grid */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <UserCircle className="h-4 w-4" />
            Keanggotaan
          </h3>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3">
              <Field label="ID Klien" value={user.id_client || "-"} className="border-b sm:border-r border-border" />
              <div className="p-4 border-b sm:border-r border-border">
                <div className="text-xs text-muted-foreground mb-1">NRI</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-foreground">{user.nri || "-"}</div>
                  {checkingPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  ) : pendingRequest ? (
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <Clock className="h-3 w-3" />
                      {pendingRequest.status === "pending" ? "Diproses" : pendingRequest.status === "approved" ? "Disetujui" : "Ditolak"}
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setClaimDialogOpen(true)}
                    >
                      <AlertCircle className="h-3 w-3" />
                      Klaim NRI
                    </Button>
                  )}
                </div>
              </div>
              <Field label="Masa Laku KTA" value={formatMasaLakuLikeKtaPdf(user.masa_laku, user.masa_laku_kta)} className="border-b border-border" />
            </div>
          </div>
        </div>

        {/* Download Section */}
        {canDownloadDocs && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Download className="h-4 w-4" />
              Unduh Dokumen
            </h3>
            <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
              <button
                type="button"
                onClick={() => handleDownload("kta")}
                disabled={downloadingKTA || !user.id || (!hasValidSdppi && !isIarKehormatan && (checkingSettlement || !hasSettlement))}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center shrink-0">
                  {downloadingKTA ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : <Download className="h-5 w-5 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">Kartu Tanda Anggota (KTA)</div>
                  <div className="text-xs text-muted-foreground">Buat ulang PDF dari data profil terkini</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleDownload("tag")}
                disabled={downloadingTag || !user.id || (!hasValidSdppi && !isIarKehormatan && (checkingSettlement || !hasSettlement))}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-green-50 dark:bg-green-950 flex items-center justify-center shrink-0">
                  {downloadingTag ? <Loader2 className="h-5 w-5 animate-spin text-green-600" /> : <Download className="h-5 w-5 text-green-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">Tag</div>
                  <div className="text-xs text-muted-foreground">Buat ulang PDF dari data profil terkini</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleDownload("callsign")}
                disabled={downloadingCallsign || !user.id || (!hasValidSdppi && !isIarKehormatan && (checkingSettlement || !hasSettlement))}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-purple-50 dark:bg-purple-950 flex items-center justify-center shrink-0">
                  {downloadingCallsign ? <Loader2 className="h-5 w-5 animate-spin text-purple-600" /> : <Download className="h-5 w-5 text-purple-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">Papan Callsign</div>
                  <div className="text-xs text-muted-foreground">Buat ulang PDF dari data profil terkini</div>
                </div>
              </button>
            </div>
          </div>
        )}
        
        <NriClaimDialog
          open={claimDialogOpen}
          onOpenChange={setClaimDialogOpen}
          currentNri={user.nri}
          callsign={user.callsign}
          onSuccess={checkPendingRequest}
        />
      </CardContent>
    </Card>
  );
}
